<template>
    <page :busy="busy" class="damage-protection-edit-page">
        <b-container fluid v-if="claimData">
            <validation-observer ref="account_form" class="d-flex flex-column" v-slot="{ errors, setFieldValue }">
                <b-row class="mt-2">
                    <b-col class="mb-2" cols="12" md="6" xl="3">
                        <b-card class="template sidebar-header-card">
                            <template #header>Claim Details</template>
                            <b-card-text class="d-flex justify-content-between align-items-center">
                                <span>Claim #:</span>
                                <validation-provider name="claim_number" rules="required" v-if="userIsAdmin">
                                    <form-input
                                        v-model="claimNumber"
                                        class="mb-0 claim-form-field"
                                        :disabled="claimData.is_sdw"
                                        :error="errors.claim_number"
                                        @input="(val) => setFieldValue('claim_number', val)"
                                    />
                                </validation-provider>
                                <span v-else>{{ claimData.claim.number }}</span></b-card-text
                            >
                            <b-card-text class="d-flex justify-content-between align-items-center"
                                ><span>Status:</span>
                                <form-select
                                    v-if="userIsAdmin"
                                    :options="statusList"
                                    v-model="selectedClaimStatus"
                                    class="mb-0 claim-form-field"
                                />
                                <span v-else>{{ claimData.claim.status_name }}</span></b-card-text
                            >
                            <b-card-text v-if="userIsAdmin" class="d-flex justify-content-between mt-1 mb-1">
                                <span>Subscriber ID:</span>
                                <span>{{ claimData.subscriber_id }}</span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between" v-if="userIsAdmin">
                                <span>Subscriber: </span>
                                <span>{{ claimData.subscriber_company }}</span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span>Opened: </span>
                                <span>
                                    {{ convertToUserTime(claimData.claim.submitted_date, true) }}
                                </span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span>Updated:</span>
                                <span>{{ convertToUserTime(claimData.claim.updated, true) }}</span>
                            </b-card-text>
                        </b-card>
                    </b-col>
                    <b-col class="mb-2" cols="12" md="6" xl="3">
                        <b-card class="sidebar-header-card">
                            <template #header>Policy Details</template>

                            <b-card-text class="d-flex justify-content-between">
                                <span>Policy #:</span>
                                <span> {{ claimData.external_policy_id }}</span></b-card-text
                            >
                            <b-card-text class="d-flex justify-content-between"
                                ><span>Product: </span><span>{{ claimData.product_type }}</span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span>Purchase Date:</span>
                                <span>{{ convertToUserTime(claimData.created) }}</span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span>Effective Date:</span>
                                <span>{{ convertToUserTime(claimData.from_date) }}</span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span> Coverage Ends: </span>
                                <span>
                                    {{ convertToUserTime(claimData.to_date) }}
                                </span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span> Cost: </span>
                                <span>
                                    {{ convertToAmount(claimData.value, currencySymbol) }}
                                </span>
                            </b-card-text>
                            <responsive-button
                                v-if="userIsAdmin"
                                @click="$bvModal.show(adminSendEmailModalId)"
                                :icon="['fas', 'envelope']"
                                variant="primary"
                                >Email</responsive-button
                            >
                        </b-card>
                    </b-col>
                    <b-col class="mb-2" cols="12" md="6" xl="3">
                        <b-card class="template sidebar-header-card">
                            <template #header>Policy Holder</template>

                            <b-card-text class="d-flex justify-content-between">
                                {{ claimData.reservation.user.full_name }}</b-card-text
                            >
                            <b-card-text class="d-flex justify-content-between">
                                {{ claimData.reservation.user.address.address1 }}</b-card-text
                            >
                            <b-card-text
                                class="d-flex justify-content-between"
                                v-if="claimData.reservation.user.address.address2"
                            >
                                {{ claimData.reservation.user.address.address2 }}</b-card-text
                            >
                            <b-card-text class="d-flex justify-content-between">
                                {{ claimData.reservation.user.address.short_name }}
                                &nbsp;
                                {{ claimData.reservation.user.address.zip }}
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                {{ claimData.reservation.user.address.phone }}
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                {{ claimData.reservation.user.email }}
                            </b-card-text>
                        </b-card>
                    </b-col>
                    <b-col class="mb-2" cols="12" md="6" xl="3">
                        <b-card class="sidebar-header-card">
                            <template #header>Reservation Details</template>

                            <b-card-text class="d-flex justify-content-between">
                                <span> Booking Date: </span>
                                <span>
                                    {{ convertToUserTime(claimData.reservation.created) }}
                                </span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span> Booking Number: </span>
                                <span>{{ claimData.reservation.id }}</span></b-card-text
                            >
                            <b-card-text class="d-flex justify-content-between">
                                <span> Booking Total: </span>
                                <span>{{ claimData.booking_total }}</span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span> Check In: </span>
                                <span>
                                    {{ convertToUserTime(claimData.reservation.from_date) }}
                                </span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span> Check Out: </span>
                                <span>{{ convertToUserTime(claimData.reservation.to_date) }}</span>
                            </b-card-text>
                            <b-card-text class="d-flex justify-content-between">
                                <span> Property: </span>
                                <div class="d-flex flex-column text-right">
                                    <span>{{ claimData.property_name }}</span>
                                </div>
                            </b-card-text>
                            <b-card-text v-if="claimData.room_name" class="d-flex justify-content-between">
                                <span> Unit: </span>
                                <div class="d-flex flex-column text-right">
                                    <span>{{ claimData.room_name }}</span>
                                </div>
                            </b-card-text>
                        </b-card>
                    </b-col>
                </b-row>
                <b-row class="flex-md-row flex-column">
                    <b-col cols="12" md="4">
                        <validation-provider name="damage_reporter" rules="required">
                            <form-select
                                v-model="selectedDamageOption"
                                :options="damageOptions"
                                label="How was damage reported?"
                                :error="errors.damage_reporter"
                                :disabled="claimData.read_only"
                                @change="(val) => setFieldValue('damage_reporter', val)"
                            />
                        </validation-provider>
                    </b-col>
                    <b-col cols="12" md="4">
                        <validation-provider name="date_reported" rules="required">
                            <form-datepicker
                                v-model="damageDate"
                                label="Date damage was reported:"
                                :hide-header="true"
                                :display-format="userUnicodeDateFormat"
                                :error="errors.date_reported"
                                :disabled="claimData.read_only"
                                @input="(val) => setFieldValue('date_reported', val)"
                            />
                        </validation-provider>
                    </b-col>

                    <b-col cols="12" md="4">
                        <validation-provider name="amount" rules="required|validFloat">
                            <form-input
                                v-model="amount"
                                label="Claim Amount (USD):"
                                :error="errors.amount"
                                :disabled="claimData.read_only"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12">
                        <validation-provider name="nature_of_incident" rules="required">
                            <form-textarea
                                v-model="natureOfIncident"
                                label="Nature of Incident that caused the damage:"
                                rows="4"
                                :error="errors.nature_of_incident"
                                :disabled="claimData.read_only"
                                @input="(val) => setFieldValue('nature_of_incident', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col cols="12">
                        <validation-provider name="corrective_action" rules="required">
                            <form-textarea
                                v-model="correctiveAction"
                                label="Corrective action to be taken:"
                                rows="4"
                                :error="errors.corrective_action"
                                :disabled="claimData.read_only"
                                @input="(val) => setFieldValue('corrective_action', val)"
                            />
                        </validation-provider>
                    </b-col>
                </b-row>
            </validation-observer>
            <b-row>
                <b-col cols="12">
                    <div class="grid-caption">Uploaded Documentation</div>
                    <uploaded-documentation-table
                        :rowData="uploadedDocuments"
                        @table-updated="fetchUploadedDocuments"
                        :totalAttachmentSize="totalAttachmentSize"
                    />
                </b-col>
            </b-row>
            <b-row v-if="userIsAdmin" class="pt-3">
                <b-col cols="12">
                    <div class="grid-caption" @click="$bvModal.show(adminNoteModalId)">
                        Administrative Log
                        <font-awesome-icon class="text-primary ml-2 cursor-pointer" :icon="['fas', 'note-sticky']" />
                    </div>
                    <damage-protection-admin-logs-table :rowData="adminLogs" />
                </b-col>
            </b-row>
            <b-row v-if="userIsAdmin" class="pt-3">
                <b-col cols="12">
                    <div class="grid-caption">Account History</div>
                    <damage-protection-account-history-table :rowData="accountHistory" />
                </b-col>
            </b-row>
            <b-row class="mt-3 mb-5" v-if="!claimData.read_only">
                <b-col cols="12">
                    <b-card class="template sidebar-header-card">
                        <template #header>Upload Documentation</template>
                        <div class="d-flex flex-column flex-md-row">
                            <div class="d-flex flex-column justify-content-between mr-4 mb-4 mb-md-0">
                                <ul class="pl-3 upload-notes-list">
                                    <li>Photographs of the damage to property.</li>
                                    <li>Replacement receipt or PAID invoice.</li>
                                    <li>Theft Claim - requires a copy of police report filed.</li>
                                </ul>
                                <div class="d-flex flex-md-row flex-column">
                                    <b-button
                                        class="mr-md-2 mb-1 mb-md-0"
                                        variant="success"
                                        @click.prevent="showConfirmationModal(true)"
                                    >
                                        Submit Claim
                                    </b-button>
                                    <b-button
                                        v-if="claimData.is_sdw"
                                        class="mr-md-2 mb-1 mb-md-0"
                                        variant="primary"
                                        @click.prevent="showConfirmationModal(false)"
                                    >
                                        Update Claim
                                    </b-button>
                                    <b-button :href="getBackLink" variant="warning"> Return </b-button>
                                </div>
                            </div>
                            <div class="d-flex flex-column">
                                <validation-observer
                                    ref="upload_documents"
                                    v-slot="{ errors, setFieldValue, meta }"
                                    class="d-flex flex-column"
                                >
                                    <validation-provider name="description" rules="required">
                                        <form-input
                                            v-model="documentTitle"
                                            label="Document Title"
                                            :error="errors.description"
                                            @input="(val) => setFieldValue('description', val)"
                                        />
                                    </validation-provider>
                                    <validation-provider name="dtype" rules="required">
                                        <form-select
                                            :options="documentTypeOptions"
                                            label="Document Type"
                                            v-model="selectedDocumentType"
                                            :error="errors.dtype"
                                            @change="(val) => setFieldValue('dtype', val)"
                                        />
                                    </validation-provider>
                                    <validation-provider name="document" rules="required">
                                        <form-file
                                            accept=".png, .jpg, .jpeg, .gif, .pdf"
                                            v-model="selectedFile"
                                            label="Document File"
                                            :error="errors.document"
                                            @input="(val) => setFieldValue('document', val)"
                                        />
                                    </validation-provider>
                                    <small>Note: PDF, PNG, JPEG AND GIF documents only. </small>
                                    <b-button @click="confirmUpload" :disabled="!meta.valid" variant="primary"
                                        >Upload Document</b-button
                                    >
                                </validation-observer>
                            </div>
                        </div>
                    </b-card>
                </b-col>
            </b-row>
        </b-container>
        <damage-protection-send-email-modal
            :modal-id="adminSendEmailModalId"
            :initial-values="claimData"
            @emailSent="fetchAdminLogs"
        />
        <modal
            :id="noDocumentsModalId"
            title="Please Confirm"
            ok-title="Open Claim, Submit Documentation Later"
            @ok.prevent="saveOrSubmit(true)"
        >
            This claim is not complete as it does not contain the required documentation; which is an image/pdf of a
            receipt or invoice and one image of the damage to the property. A claim can be opened without the required
            documentation. However, you MUST upload the required documentation within 45 days of opening a claim or the
            claim will expire and you will not get reimbursed.
        </modal>
        <modal :id="confirmationSubmitModalId" title="Please Confirm" ok-title="Confirm" @ok.prevent="saveOrSubmit()">
            This action is FINAL! Claims cannot be updated after submission for payment.
        </modal>
        <modal :id="confirmationUpdateModalId" title="Please Confirm" ok-title="Confirm" @ok.prevent="saveOrSubmit()">
            Claim will be updated with new information. Claims can only submitted for reimbursement by using the SUBMIT
            CLAIM button.
        </modal>
        <modal
            :id="confirmationUploadDocModalId"
            title="Confirm Upload"
            ok-title="Proceed with Upload"
            @ok.prevent="uploadDocuments"
        >
            STOP! Do not upload this invoice unless it is an official PAID invoice. The invoice must show:<br /><br />
            1) that the work has been completed and paid.<br />2) an invoice date that is after the date the damage was
            reported.<br /><br />Failure to follow these simple rules will result in your SDW reimbursement request
            getting denied or delayed.
        </modal>
        <modal :id="adminNoteModalId" title="Create Note" ok-title="Save" @ok.prevent="createNote">
            <form-textarea v-model="adminNote" :rows="3" />
        </modal>
    </page>
</template>

<script>
import api from '../../../api';
import { formatResponseError } from '../../../utils/api';
import isNaN from 'lodash/isNaN';
import capitalize from 'lodash/capitalize';
import Decimal from 'decimal.js';
import isEmpty from 'lodash/isEmpty';
import {
    ADMIN_DAMAGE_CLAIMS_LIST_ENDPOINT,
    ADMIN_DAMAGE_CLAIMS_LOGS_ENDPOINT,
    DAMAGE_PROTECTION_EDIT_CLAIM_ENDPOINT,
    ADMIN_DAMAGE_CLAIMS_ACCOUNT_HISTORY_ENDPOINT,
} from '../../../endpoints';
import page from '../../generic/page/page.vue';
import { mapGetters } from 'vuex';
import format from 'date-fns/format';
import parseISO from 'date-fns/parseISO';
import { toAmountDisplay } from '../../../utils/numbers';
import FormSelect from '../../generic/form-select/form-select.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import FormFile from '../../generic/form-file/form-file.vue';
import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
import ResponsiveButton from '../../generic/responsive-button/responsive-button.vue';
import DamageProtectionSendEmailModal from './damage-protection-send-email-modal.vue';
import Modal from '../../generic/modal/modal.vue';

import FormDatepicker from '../../generic/form-datepicker/form-datepicker.vue';
import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
import UploadedDocumentationTable from './tables/uploaded-documentation-table.vue';
import DamageProtectionAdminLogsTable from './tables/damage-protection-admin-logs-table.vue';
import DamageProtectionAccountHistoryTable from './tables/damage-protection-account-history-table.vue';

export default {
    name: 'DamageClaimEdit',
    components: {
        page,
        FormSelect,
        FormDatepicker,
        FormInput,
        FormTextarea,
        UploadedDocumentationTable,
        FormFile,
        Modal,
        DamageProtectionAdminLogsTable,
        FormCheckbox,
        ResponsiveButton,
        DamageProtectionAccountHistoryTable,
        DamageProtectionSendEmailModal,
    },
    data() {
        return {
            busy: false,
            tabs: [
                {
                    active: true,
                    title: 'Claim',
                },
            ],
            claimData: null,
            damageDate: null,
            amount: null,
            damageOptions: [],
            selectedDamageOption: null,
            natureOfIncident: null,
            correctiveAction: null,
            uploadedDocuments: [],
            documentTypeOptions: [],
            isSubmit: false,
            documentTitle: '',
            selectedDocumentType: '',
            selectedClaimStatus: null,
            selectedFile: null,
            adminLogs: [],
            adminNote: null,
            statusList: [],
            claimNumber: '',
            accountNumber: null,
            totalAttachmentSize: 0,
            currencySymbol: '$',
            accountHistory: [],
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', 'userTimeFormat', 'userCurrencySymbol', 'userIsAdmin']),
        claimId() {
            return parseInt(this.$route.params.id);
        },
        getPropertyString() {
            return this.claimData && this.claimData.properties.map((item) => item.name);
        },
        getBackLink() {
            return this.userIsAdmin ? '/app/admin/damage-claims/' : '/app/damage-protection/issuances/';
        },
        noDocumentsModalId() {
            return 'no-documents-modal';
        },
        confirmationSubmitModalId() {
            return 'confirmation-submit-modal';
        },
        confirmationUpdateModalId() {
            return 'confirmation-update-modal';
        },
        confirmationUploadDocModalId() {
            return 'confirmation-upload-doc-modal';
        },
        adminNoteModalId() {
            return 'admin-note-modal';
        },
        adminSendEmailModalId() {
            return 'admin-send-email-modal';
        },
    },
    mounted() {
        if (!isNaN(this.claimId)) {
            (async () => {
                this.busy = true;
                await this.fetchClaimData();
                await this.fetchDamageOptions();
                await this.fetchUploadedDocuments();
                await this.fetchDocumentTypeOptions();
                if (this.userIsAdmin && this.claimData) {
                    await this.fetchAdminLogs();
                    await this.fetchClaimStatusList();
                    await this.fetchAccountHistory();
                }
                this.$refs.account_form.setValues({
                    claim_number: this.claimNumber,
                    damage_reporter: this.selectedDamageOption,
                    date_reported: this.damageDate,
                    nature_of_incident: this.natureOfIncident,
                    corrective_action: this.correctiveAction,
                    amount: this.amount,
                });
                this.busy = false;
            })();
        }
    },
    methods: {
        convertToUserTime(value, isTime) {
            return (
                value && format(parseISO(value), `${this.userUnicodeDateFormat} ${isTime ? this.userTimeFormat : ''}`)
            );
        },
        convertToAmount(value, currency) {
            return (
                value &&
                toAmountDisplay({
                    amount: Number(value),
                    currency: currency || this.userCurrencySymbol,
                })
            );
        },
        async fetchAdminLogs() {
            try {
                const { data } = await api.get(ADMIN_DAMAGE_CLAIMS_LOGS_ENDPOINT(this.claimData.claim.id));
                
                data.forEach(item => {
                    if (item.operation === 'Note Added') {
                        item.notes = item.body;
                        item.body = '';
                    }
                });
                
                this.adminLogs = data;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch logs' });
            }
        },
        async fetchDamageOptions() {
            try {
                const { data } = await api.get(`${DAMAGE_PROTECTION_EDIT_CLAIM_ENDPOINT()}damage-options/`);
                this.damageOptions = data.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
                const isStatusExists = data.find((item) => item.id === this.selectedDamageOption);
                this.selectedDamageOption = isStatusExists ? this.selectedDamageOption : data.length && data[0].id;
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch damage options' });
            }
        },
        async fetchAccountHistory() {
            try {
                const { data } = await api.get(
                    `${ADMIN_DAMAGE_CLAIMS_ACCOUNT_HISTORY_ENDPOINT()}?insurance_pk=${this.claimId}`
                );
                this.accountHistory = [data];
            } catch (e) {
                this.errorToast({ text: 'Could not fetch account history' });
            }
        },
        async createNote() {
            try {
                this.busy = true;
                await api.post(ADMIN_DAMAGE_CLAIMS_LOGS_ENDPOINT(), {
                    claim_id: this.claimData.claim.id,
                    body: this.adminNote,
                });
                await this.fetchAdminLogs();
                this.successToast({ text: 'Note was created' });
                this.$bvModal.hide(this.adminNoteModalId);
            } catch (e) {
                this.errorToast({ text: 'Could not create note' });
            } finally {
                this.busy = false;
            }
        },
        async confirmUpload() {
            const invoiceDocumentType = 2018;
            if (this.selectedDocumentType === invoiceDocumentType) {
                this.$bvModal.show(this.confirmationUploadDocModalId);
            } else {
                this.uploadDocuments();
            }
        },
        async fetchClaimStatusList() {
            try {
                const { data } = await api.get(
                    `${ADMIN_DAMAGE_CLAIMS_LIST_ENDPOINT()}statuses/?is_sdw=${this.claimData.is_sdw}`
                );
                this.statusList = [
                    ...data.map((item) => ({
                        value: item.id,
                        text: item.name,
                    })),
                ];
                const isStatusExists = data.find((item) => item.id === this.selectedClaimStatus);
                this.selectedClaimStatus = isStatusExists ? this.selectedClaimStatus : data[0].id;
            } catch (e) {
                this.errorToast({ text: 'Could not load statuses' });
            }
        },
        async uploadDocuments() {
            try {
                this.busy = true;
                const body = new FormData();
                const fileName = this.selectedFile.name;
                body.append('document', this.selectedFile);
                body.append('dtype', this.selectedDocumentType);
                body.append('description', this.documentTitle);
                body.append('filename', fileName);
                await api.post(`${DAMAGE_PROTECTION_EDIT_CLAIM_ENDPOINT(this.claimId)}upload-document/`, body, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });
                await this.fetchUploadedDocuments();
                this.selectedDocumentType === 2018 && this.$bvModal.hide(this.confirmationUploadDocModalId);
                this.successToast({ text: 'Document uploaded successfully' });
            } catch (e) {
                const { generic, fields } = formatResponseError(e);

                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.$refs.upload_documents.setErrors(fields);
                }
                if (e.response?.data && e.response.data.error) {
                    this.errorToast({ text: e.response.data.error });
                } else {
                    this.errorToast({
                        text: 'Could not upload document',
                    });
                }
            } finally {
                this.busy = false;
            }
        },
        async fetchDocumentTypeOptions() {
            try {
                const { data } = await api.get(`${DAMAGE_PROTECTION_EDIT_CLAIM_ENDPOINT()}document-type-options/`);
                this.documentTypeOptions = data.map((item) => ({
                    value: item.id,
                    text: item.name,
                }));
                this.selectedDocumentType = data.length && data[0].id;
                this.$refs.upload_documents.setValues({
                    dtype: this.selectedDocumentType,
                });
            } catch (e) {
                this.errorToast({ text: 'Could not fetch document type options' });
            }
        },
        async fetchUploadedDocuments() {
            try {
                const { data } = await api.get(
                    `${DAMAGE_PROTECTION_EDIT_CLAIM_ENDPOINT(this.claimId)}documents-uploads/`
                );
                this.uploadedDocuments = data.data;
                this.totalAttachmentSize = data.total;
            } catch (e) {
                this.errorToast({ text: 'Could not fetch uploaded documents' });
            }
        },
        async validateForm() {
            const data = await this.$refs.account_form.validate();
            if (data.valid) {
                return true;
            } else {
                this.triggerErrors(data.errors);
            }
        },
        async fetchClaimData() {
            try {
                const { data } = await api.get(DAMAGE_PROTECTION_EDIT_CLAIM_ENDPOINT(this.claimId));
                this.claimData = data;
                this.amount = new Decimal(data.claim.amount).toFixed(2);
                this.damageDate = data.claim.date_reported
                    ? format(parseISO(data.claim.date_reported), 'yyyy-MM-dd')
                    : null;
                this.selectedDamageOption = data.claim.damage_reporter;
                this.natureOfIncident = data.claim.nature_of_incident;
                this.correctiveAction = data.claim.corrective_action;
                this.selectedClaimStatus = data.claim.status_id;
                const claimNumber = data.claim.number.split('-')[0];
                this.claimNumber = claimNumber;
                this.accountNumber = data.claim.account_number;
            } catch (e) {
                console.log(e);
                this.errorToast({ text: 'Could not fetch claim' });
            }
        },
        async showConfirmationModal(isSubmit) {
            this.isSubmit = isSubmit;
            const isValid = await this.validateForm();
            if (isValid) {
                if (!this.claimData.is_sdw) {
                    this.isSubmit = true;
                    this.saveOrSubmit();
                } else {
                    this.$bvModal.show(isSubmit ? this.confirmationSubmitModalId : this.confirmationUpdateModalId);
                }
            }
        },
        triggerErrors(errors) {
            Object.keys(errors).forEach((item) => {
                errors[item].length &&
                    this.errorToast({
                        text: `${capitalize(item.replaceAll('_', ' '))}: ${errors[item]}`,
                    });
            });
        },
        async saveOrSubmit(force = false) {
            try {
                this.busy = true;
                const body = {
                    amount: this.amount,
                    date_reported: this.damageDate,
                    damage_reporter: this.selectedDamageOption,
                    nature_of_incident: this.natureOfIncident,
                    corrective_action: this.correctiveAction,
                    submit: this.isSubmit,
                    force,
                };
                if (this.userIsAdmin) {
                    body.status = this.selectedClaimStatus;
                    body.account_number = this.accountNumber;
                    body.claim_number = this.claimNumber;
                }
                const { data } = await api.post(`${DAMAGE_PROTECTION_EDIT_CLAIM_ENDPOINT(this.claimId)}submit/`, body);
                if (data.claim_status === 'NODOCUMENTS') {
                    this.$bvModal.show(this.noDocumentsModalId);
                } else {
                    this.successToast({
                        text: `Claim successfully ${this.isSubmit ? 'submited' : 'updated'}.`,
                    });
                }
                if (this.userIsAdmin) {
                    this.fetchAdminLogs();
                }
                if (force) {
                    this.$bvModal.hide(this.noDocumentsModalId);
                }
                if (this.isSubmit) {
                    this.$bvModal.hide(this.confirmationSubmitModalId);
                }
                if (!this.isSubmit) {
                    this.$bvModal.hide(this.confirmationUpdateModalId);
                }
            } catch (e) {
                const { generic, fields } = formatResponseError(e);

                if (generic) {
                    this.errorToast({ text: generic });
                }
                if (!isEmpty(fields)) {
                    this.triggerErrors(fields);
                    this.$refs.account_form.setErrors(fields);
                }
                if (e.response.data && e.response.data.error) {
                    this.errorToast({ text: e.response.data.error });
                } else {
                    this.errorToast({
                        text: `Could not ${this.isSubmit ? 'submit' : 'update'} claim`,
                    });
                }
            } finally {
                this.busy = false;
            }
        },
    },
};
</script>

<style lang="scss" scoped>
:deep(*) {
    font-family: 'Lato', sans-serif !important;
}

.grid-caption {
    color: #3d3d3d;
    font-size: 18px;
    width: auto;
    padding: 3px;
    padding-left: 0;
    font-weight: bold;
}

.sidebar-header-card {
    margin-bottom: 10px !important;
    height: 100%;

    :deep(.card-header) {
        background-color: #0d47a1 !important;
        color: #fff;
        padding: 10px !important;
        font-weight: bold;
        align-items: center;
        justify-content: left !important;
        min-height: 50px;
        font-size: 18px;
        padding-left: 15px !important;
    }

    :deep(.card-footer) {
        padding: 5px !important;
    }

    :deep(.card-body) {
        padding: 10px;
        padding-top: 10px !important;
    }

    :deep(.card-subtitle) {
        margin-top: 0;
        background-color: #dfdfdf;
        padding: 5px;
    }

    :deep(.card-title) {
        margin-bottom: 0.5rem !important;
    }
}

.damage-protection-edit-page {
    background-color: #edf0f6 !important;
}
.upload-notes-list {
    font-size: 15px;
}

.damage-protection-edit-page {
    :deep(.card-text) {
        margin-bottom: 0.3rem !important;
    }
    :deep(.table-header-highlight) {
        .ag-header-row-column-group,
        .ag-header-row-column {
            background: #0d47a1;
            color: #fff;
            .ag-header-cell-text {
                color: #fff;
            }
        }
        .ag-root-wrapper {
            padding: 0;
        }
    }
}
</style>
